import { useEffect, useState } from "react";
import { api, apiError, money } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { DownloadSimple, Receipt } from "@phosphor-icons/react";

const YEARS = Array.from({ length: 4 }, (_, i) => new Date().getFullYear() - i);

export default function AdminStatements() {
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/statements", { params: { year } })
      .then(({ data: d }) => setData(d))
      .catch((e) => toast.error(apiError(e.response?.data?.detail)));
  }, [year]);

  const download = async (payer) => {
    try {
      const { data: blob } = await api.get(
        `/statements/${year}?payer_email=${encodeURIComponent(payer)}&format=csv`,
        { responseType: "blob" });
      const href = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = href;
      a.download = `tax_statement_${year}_${payer.split("@")[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(href);
      toast.success("Statement downloaded");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  return (
    <div data-testid="admin-statements-page">
      <p className="text-sm uppercase tracking-[0.2em] font-bold text-[#3B82F6]">Tax Records</p>
      <h1 className="mt-3 text-4xl font-bold tracking-tight">Year-end family statements</h1>
      <p className="mt-3 text-muted-foreground max-w-2xl">
        What each family paid over the year, ready to hand out for childcare tax credits. Families can
        also download their own copy from their portal.
      </p>

      <div className="mt-8 w-40">
        <Select value={year} onValueChange={setYear}>
          <SelectTrigger className="h-12 rounded-xl bg-white" data-testid="admin-statement-year"><SelectValue /></SelectTrigger>
          <SelectContent>{YEARS.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {!data ? (
        <p className="mt-10 text-muted-foreground">Loading…</p>
      ) : (
        <>
          <div className="mt-6 bg-white rounded-2xl border border-border p-6 inline-block"
               data-testid="statements-total">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">
              Total collected in {data.year}
            </p>
            <p className="mt-3 font-display text-3xl font-bold text-[#10B981]">{money(data.total_collected)}</p>
          </div>

          <div className="mt-8 space-y-3" data-testid="statements-payers">
            {data.payers.length === 0 && (
              <div className="bg-white rounded-3xl border border-border p-12 text-center" data-testid="statements-empty">
                <Receipt size={56} weight="duotone" className="mx-auto text-[#3B82F6]" />
                <p className="mt-6 font-display text-xl font-semibold">No payments recorded in {data.year}</p>
              </div>
            )}
            {data.payers.map((p) => (
              <div key={p.payer_email} className="bg-white rounded-2xl border border-border p-5 flex flex-wrap gap-4 items-center"
                   data-testid={`statement-payer-${p.payer_email}`}>
                <div className="min-w-0 flex-1">
                  <p className="font-bold">{p.payer_name || p.payer_email}</p>
                  <p className="text-xs text-muted-foreground mt-1 font-semibold">
                    {p.payer_email} · {p.children.join(", ")} · {p.payment_count} payment{p.payment_count === 1 ? "" : "s"}
                  </p>
                </div>
                <p className="font-display text-xl font-bold">{money(p.total_paid)}</p>
                <Button size="sm" onClick={() => download(p.payer_email)}
                        className="rounded-full bg-[#3B82F6] hover:bg-[#2563EB] font-bold"
                        data-testid={`download-statement-${p.payer_email}`}>
                  <DownloadSimple size={16} className="mr-1" /> CSV
                </Button>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
